/**
 * Core typed GameState for Strategos Universalis v1.
 *
 * This module defines the *authoritative* simulation state. The renderer reads
 * these types but never mutates them. Every mutation flows through a Command
 * (see src/game/commands.ts) so the simulation is reproducible on a peer host
 * given the same seed + command stream (MULTIPLAYER §2).
 *
 * Numeric ranges use unbranded `number` for ease of serialisation — invariants
 * are enforced in sim code, not in the type system.
 */

import type { RngState } from './rng';

// ---------------------------------------------------------------------------
// Primitive identifiers (entity IDs are issued as monotonically increasing
// uint32s by the sim; seeding uses the RNG state).
// ---------------------------------------------------------------------------

export type EntityId = number;
export type EmpireId = number;
export type PlanetId = number;
export type SystemId = number;
export type ShipInstanceId = number;
export type JumpPointId = number;
export type WarpGateLinkId = number;
/** v3M28 — id alias for persistent missile entities (`docs/MISSILES.md`). */
export type MissileId = number;

/**
 * The detection subsystem aggregates vision per *team* rather than per empire
 * so future alliances can share sight without reworking every call site. In
 * v1 there are no alliances, so TeamId is just the owning EmpireId; every
 * empire is its own one-member team. Flip to a real team mapping later
 * without touching the detection math (MECHANICS §3, MULTIPLAYER §2).
 */
export type TeamId = EmpireId;

// ---------------------------------------------------------------------------
// Enumerations (canonical string unions)
// ---------------------------------------------------------------------------

export type RaceId =
  | 'RACE-AA'
  | 'RACE-BB'
  | 'RACE-CC'
  | 'RACE-DD'
  | 'RACE-EE'
  /** v3M21.1 — synthetic neutral empire's race tag. Never participates
   * in research, build orders, AI dispatch, or scoring. Reserved for
   * the row at `EMPIRE_NEUTRAL_ID` only. */
  | 'Neutral';

export type ResourceKind =
  | 'credits'
  | 'research'
  | 'minerals'
  | 'population'
  | 'diplomatic'
  | 'fuel';

export type BiomeKind =
  | 'gaia'
  | 'temperate'
  | 'desert'
  | 'arctic'
  | 'ocean'
  | 'volcanic'
  | 'barren';

/**
 * v3M7.1 — AI difficulty + behaviour names. Authored in `AI_Behaviours.xlsx`
 * (Difficulty + Behaviours sheets) and surfaced as plain string-typed
 * union members keyed by name. The runtime resolver in `balance-ai.ts`
 * looks up tuning rows by these strings; a typo at the data layer would
 * fall through to the default-row fallback (Normal / Balanced).
 */
export type AiDifficulty = 'Easy' | 'Normal' | 'Hard';
export type AiBehaviour =
  | 'Expansionist'
  | 'Economist'
  | 'Technologist'
  | 'Fortifier'
  | 'Aggressive'
  | 'Balanced'
  /** v3M21.1 — assigned to the synthetic neutral empire only. The
   * AI dispatch loop filters this row out via the `EMPIRE_NEUTRAL_ID`
   * guard, so the value is documentation more than mechanism. */
  | 'Neutral';

/**
 * v3M21.5 — runtime race catalogue mirroring `LoadedGameData['races']`.
 * Carries the per-race diplomatic aggression multiplier so the AI
 * dispatcher can compute effective aggression at tick time without
 * needing to thread `LoadedGameData` through every helper.
 */
export interface RaceDef {
  raceId: string;
  name: string;
  status: 'v1' | 'v2';
  description: string;
  characteristics: readonly string[];
  diplomaticAggressionMultiplier: number;
}

/**
 * v3M7.1 — AI behaviour data shape mirroring the convert-xlsx output for
 * `AI_Behaviours.xlsx`. Stored on `LoadedGameData` and `GameState` like
 * the other static catalogues.
 */
export interface AiBehaviourRow {
  aggressive: boolean;
  defaultCloak: boolean;
  coloniserPriority: number;
  jumpWillingness: number;
  retreatHullThreshold: number;
  /**
   * v3M21.5 — per-behaviour diplomatic aggression in [0, 1]. Multiplied
   * by the race's `diplomaticAggressionMultiplier` (Ships.xlsx Races
   * sheet) to produce the empire's effective aggression. Below the
   * `pickEpsilon` floor (default 0.1) the AI ignores diplomacy
   * entirely. Above it, the AI scans visible neutrals each tick and
   * spends Diplomatic resource per `DIPLOMACY.md §9`.
   */
  diplomaticAggression: number;
  notes: string;
}

export interface AiDifficultyRow {
  tickInterval: number;
  startingResourceBonus: number;
  perTickResourceBonus: number;
  notes: string;
}

export interface AiBehavioursData {
  behaviours: Record<string, AiBehaviourRow>;
  difficulties: Record<string, AiDifficultyRow>;
  structurePriorityByBehaviour: Record<string, string[]>;
  shipPriorityByRaceBehaviour: Record<string, Record<string, string[]>>;
  researchPriorityByBehaviour: Record<string, string[]>;
  spaceStructurePriorityByBehaviour: Record<string, string[]>;
}

/**
 * v3M14 — Structures.xlsx data shape. One `StructureRow` per planet
 * structure kind (Shipyard, Cloning Center, Bank, Embassy, Planetary
 * Shields, …). Per-level numbers (cost / ticks / hull / economy
 * multiplier) are 6-element arrays indexed `[level - 1]`. Kinds with
 * shorter ladders (Embassy L2, Planetary Shields L5) leave higher
 * indices undefined; consumers clamp to `maxLevel` before reading.
 *
 * v3M14a migrates planet-side tunables; v3M14b extends with
 * `SpaceStructureRow` for Constructor-built kinds and feature-specific
 * blocks (ODP ring, Minefield mechanics, Warp Gate mesh, LRSA scan
 * boost). Empire flat-resource caps and the diplomatic cap live at
 * the top level alongside the per-kind table.
 */
/**
 * v3M27 — discriminated union for the unified structure-effects schema.
 * Replaces the implicit `economyMultiplierByLevel` (where the affected
 * resource was hardcoded by `kind`) and the scattered top-level globals
 * (`embassyDiplomacyByLevel`, `storageCapPerLevel`) with explicit
 * per-row effect entries. Each structure can have multiple effects
 * (e.g. a Trade Center could declare a Credits multiplier AND a flat
 * per-tick Credits income).
 *
 * Stack semantics are baked into each variant so consumers don't have
 * to re-derive them:
 *
 *   - `maxPerPlanet`     — the highest matching effect on a single
 *                          planet wins; multiples on the same planet
 *                          DON'T compound.
 *   - `sumPerPlanet`     — multiple matching effects on a single
 *                          planet sum (e.g. two LRSA contributions
 *                          stack their scan boosts).
 *   - `sumAcrossEmpire`  — every matching effect on every owned planet
 *                          sums to a single empire-wide total
 *                          (storage caps, treasury income).
 */
export type StructureEffect =
  | {
      kind: 'multiplier';
      resource: 'credits' | 'minerals' | 'research' | 'fuel' | 'popGrowth';
      perLevel: number[];
      stack: 'maxPerPlanet';
    }
  | {
      kind: 'perTickIncome';
      resource: 'credits' | 'minerals' | 'research' | 'fuel' | 'diplomatic';
      perLevel: number[];
      stack: 'sumAcrossEmpire';
    }
  | {
      kind: 'capBonus';
      resource: 'credits' | 'minerals' | 'fuel';
      perLevel: number[];
      stack: 'sumAcrossEmpire';
    }
  | {
      kind: 'bombardmentReduction';
      perLevel: number[];
      stack: 'maxPerPlanet';
    }
  | {
      kind: 'scanRange';
      perLevel: number[];
      stack: 'sumPerPlanet';
    }
  | {
      kind: 'shipyardClassGate';
      /** Length 6: max ship-class buildable at each structure level. */
      perLevel: number[];
      stack: 'maxPerPlanet';
    }
  | {
      kind: 'weaponEmitter';
      category: 'energy' | 'kinetic' | 'concussion';
      /** Slot count contributed at each level (length 6). Multiple
       *  emitter structures on a planet sum their slots. */
      slotCountPerLevel: number[];
      stack: 'sumPerPlanet';
    };

export interface StructureRow {
  /** Internal `StructureKind` literal (e.g. `'shipyard'`). */
  kind: string;
  /** Display name (e.g. `'Shipyard'`). */
  name: string;
  /** Highest upgrade level allowed for this kind. */
  maxLevel: number;
  /**
   * Per-race max-level override. Per v3M27, every structure SHOULD
   * carry an entry per playable race (RACE-AA / BB / CC / DD / EE)
   * — set to 0 to lock a race out of the structure entirely. Missing
   * entries default to `maxLevel`. Authored either via per-race
   * columns on the Planet Structures sheet or via the legacy
   * `RaceOverrides` sheet.
   */
  maxLevelByRace: Record<string, number>;
  /** True if only one of this kind may exist per planet. */
  unique: boolean;
  /** Credits cost per upgrade step from level N → N+1. */
  buildCreditsPerLevel: number;
  /** Minerals cost per upgrade step from level N → N+1. */
  buildMineralsPerLevel: number;
  /** Build/upgrade time in ticks for each level (length 6). */
  buildTicksByLevel: number[];
  /** Max hull at each level (length 6). */
  hullByLevel: number[];
  /** Per-tick credits upkeep when active (level >= 1). */
  upkeepCredits: number;
  /**
   * v3M27 — per-row effects. Replaces the implicit
   * `economyMultiplierByLevel` + the scattered top-level
   * `embassyDiplomacyByLevel` / `storageCapPerLevel` globals. Empty
   * array means a structure with no economic / defensive / weapon
   * effect (e.g. a Shipyard's effect list is just one
   * `shipyardClassGate` entry). See `StructureEffect` for the union.
   */
  effects: StructureEffect[];
}

/**
 * v3M14c — Constructor-built space-structure row. Space structures
 * differ from planet structures: no levelling (one-shot build), no
 * upkeep, sometimes site-gated (Asteroid Mining requires an asteroid
 * field, Gas Giant Refinery requires a gas giant). The synthetic
 * ShipTemplate injection in `loader.ts` carries the chassis stats
 * (hull, armor, weapons) — this row carries the build economics + site
 * gate.
 */
export interface SpaceStructureRow {
  /** Internal kind literal as used in `SPACE_STRUCTURE_KINDS` (e.g. `'Sensor Buoy'`). */
  kind: string;
  /** Display name shown in the build menu. Currently same string as `kind`. */
  name: string;
  /** Credits cost to build, deducted at command-queue time. */
  buildCredits: number;
  /** Minerals cost to build. */
  buildMinerals: number;
  /** Build time in ticks once a Constructor is on-site. */
  buildTicks: number;
  /**
   * Required environmental entity under the Constructor's position.
   * `'open'` means anywhere; the other two require an `EnvironmentEntity`
   * of that kind for the build to start.
   */
  siteRequirement: 'open' | 'asteroid-field' | 'gas-giant';
}

/**
 * v3M14c — feature-specific tunables. Each block holds knobs for a
 * single mechanic that doesn't naturally fit on a `StructureRow` /
 * `SpaceStructureRow` (often because the values are scalars, ratios,
 * or per-level curves rather than per-build-step quantities). Kept as
 * a sub-object so the top-level `StructuresData` shape stays
 * empire-wide-only.
 */
export interface StructuresFeatureBlocks {
  /** Orbital Defence Platform ring deployment + rebuild config (v3M2.2). */
  odp: {
    ringCount: number;
    orbitRadius: number;
    rebuildCostCredits: number;
    rebuildCostMinerals: number;
    rebuildTicks: number;
  };
  /** Minefield mechanics (v3M2.3). */
  minefield: {
    radius: number;
    mineCountInitial: number;
    mineCountCap: number;
    mineDamage: number;
    triggerCooldownTicks: number;
  };
  /**
   * Warp Gate connection costs + build time (v3M18). Replaces the
   * v3M2.1 `meshSpeedup` knob — auto-mesh removed; player authors
   * explicit pairwise connections at the cost / build-time below.
   * `connectionCostMinerals` + `connectionCostFuel` debited at issue;
   * `connectionBuildTicks` is the duration the link sits in
   * `'building'` status before flipping to `'live'`.
   */
  warpGate: {
    connectionCostMinerals: number;
    connectionCostFuel: number;
    connectionBuildTicks: number;
  };
  /** Long Range Sensor Array per-level scan boost (v3M1). */
  lrsa: {
    /** Per-level additive scan-range bonus, length 6 (L1..L6). */
    scanBoostByLevel: number[];
  };
  /** Repair Yard friendly-hull regeneration field (M14). */
  repairYard: {
    radius: number;
    hullPerTick: number;
  };
  /** Resource generation rates for Constructor-built extractors (M14). */
  extractors: {
    asteroidMiningMineralsPerTick: number;
    gasGiantRefineryFuelPerTick: number;
  };
}

export interface StructuresData {
  /** Per-kind tunables, keyed by `StructureKind` (planet-side structures). */
  byKind: Record<string, StructureRow>;
  /**
   * Per-kind tunables for Constructor-built space structures, keyed by
   * the `SPACE_STRUCTURE_KINDS` string literal (`'Sensor Buoy'`,
   * `'Energy Weapons Platform'`, `'Warp Gate'`, etc.). v3M14c.
   */
  spaceByKind: Record<string, SpaceStructureRow>;
  /** Feature-specific tunables (ODP / Minefield / Warp Gate / LRSA / etc.). v3M14c. */
  features: StructuresFeatureBlocks;
  /** Auto-repair fraction of max hull restored per tick. */
  autoRepairProportionPerTick: number;
  /** Baseline empire resource caps with no storage structures. */
  baseCredits: number;
  baseMinerals: number;
  baseFuel: number;
  /** Hard cap on Diplomatic Influence regardless of Embassy count. */
  diplomaticCap: number;
  // v3M27 — `storageCapPerLevel` migrated into per-row `effects`
  // (capBonus on bank / warehouse / fuel-dump). `embassyDiplomacyByLevel`
  // migrated into the embassy row's `perTickIncome` effect. Both
  // removed from this top-level shape.
}

/**
 * v3M10.1 — cross-cutting balance constants migrated out of code into
 * `docs/Balance.xlsx` → `src/data/balance.json` → loader → this shape.
 * Values that previously lived as `export const` in `balance-*.ts`,
 * `combat-balance.ts`, `advance.ts`, and `state/types.ts` (POP_OUTPUT_PER_TICK)
 * are now reachable via `state.balanceData.<domain>.<key>`.
 *
 * Excluded by design (structural facts, not balance):
 *   - Chassis-name pointers (`WARP_GATE_CHASSIS` etc.)
 *   - Tech-name pointers (`CLOAKING_TECH_ID`, `MINEFIELDS_TECH` etc.)
 *   - Discriminator strings (`STATIONARY_SPECIAL_ABILITY`, `'Colonise planet'`)
 *   - Slot-name → category map (fixed by SHIPS.md spec)
 *   - Race / kind registry constants
 *   - Mk-1 hyperdrive baseline (fallback constant, not balance)
 *
 * Static post-load: dropped from saves and re-attached from
 * `LoadedGameData` on load like the other domain catalogues. No
 * SAVE_FORMAT_VERSION bump.
 */
export interface BalanceData {
  /**
   * Combat globals — global weapon-range multiplier, Concussion ammo
   * regen, and the per-damage-type shield/hull multiplier table.
   */
  combat: {
    weaponRangeScale: number;
    ammoRegenPerTick: number;
    damageTypeModifiers: {
      Energy: { shield: number; hull: number };
      Kinetic: { shield: number; hull: number };
      Concussion: { shield: number; hull: number };
      EMP: { shield: number; hull: number };
    };
  };
  /** EMP weapon mechanics — disable duration + per-Mk protection ladder. */
  emp: {
    baseDisableTicks: number;
    /** Length 6: Mk1..Mk6. Tick-reduction percentages. */
    protectionReductionByMk: number[];
    /** Default blast radius when a weapon's `blastRadius` is null. */
    defaultBlastRadius: number;
  };
  /** Bombardment + planetary defence (v2M5/v2M6 — STRUCTURES §2.2). */
  bombardment: {
    popKillFactor: number;
    batchTicks: number;
    loyaltyRegenPerBatch: number;
    loyaltyMax: number;
    /** v2M5 70/30 split between population kill and structure damage. */
    popDamageFraction: number;
    structureDamageFraction: number;
    damageReductionPerShieldLevel: number;
    maxShieldLevel: number;
    damageReductionPerBunkerLevel: number;
    maxBunkerLevel: number;
  };
  /** Detection / stealth / planet sensor baselines (MECHANICS §1.3). */
  detection: {
    lastKnownGraceTicks: number;
    firingBreaksStealthTicks: number;
    cloakEnergyDrainPerTick: number;
    /**
     * v3M5 — additional energy drain applied to a cloaked ship that
     * fires at least one weapon slot this tick. Sums on top of
     * `cloakEnergyDrainPerTick` for the firing tick. Only relevant when
     * the firing empire has researched `Advanced Cloaking` (RACE-CC L6,
     * MECHANICS §2.1) — without that tech, cloaked ships still cannot
     * fire at all.
     */
    cloakFireExtraDrainPerTick: number;
    planetVisionRange: number;
    planetScanRange: number;
    planetDetectionBase: number;
  };
  /** Movement-pass globals (sublight scale + spacing). */
  movement: {
    sublightSpeedScale: number;
    shipMinSeparation: number;
    formationSpacing: number;
  };
  /** Economy / population baselines. */
  economy: {
    /** Per-pop per-tick output. Diplomatic + population are derived. */
    popOutputPerTick: {
      credits: number;
      research: number;
      minerals: number;
      fuel: number;
    };
    /** Planet-size roll bounds (PLANETS §2.1). */
    planetSizeMin: number;
    planetSizeMax: number;
    homePlanetSize: number;
  };
  /** Colonisation tuning (MFix3 / PLANETS §2.7). */
  colonisation: {
    approachMargin: number;
    initialLoyalty: number;
  };
  /** Terraforming costs + duration (MFix4.3). */
  terraforming: {
    durationTicks: number;
    mineralsCost: number;
    fuelCost: number;
  };
  /** Ship build cadence by class (M10) + spawn ring radius. */
  build: {
    /** 6-entry ladder, indexed by classLevel 1..6. Same shape as the existing Record<ShipClass, number>. */
    ticksByClass: Record<number, number>;
    shipSpawnRingRadius: number;
  };
  /** AI cadence / patrol radius (M9). */
  ai: {
    patrolRadius: number;
    tickInterval: number;
  };
  /** Scenario environmental seeding bounds (MFix4.2 / MFix5.4). */
  scenario: {
    asteroidFieldsMin: number;
    asteroidFieldsMax: number;
    gasGiantsMin: number;
    gasGiantsMax: number;
    nebulaeMax: number;
    ionCloudsMax: number;
    radiationBeltsMax: number;
    blackHoleProbability: number;
    environmentEntityRadius: number;
  };
  /**
   * v3M21.1 — Diplomacy tunables (`DIPLOMACY.md §3, §5, §6`). Pre-staged
   * with v3M21.3+ outcome thresholds + Trade per-tick rates so the xlsx
   * schema doesn't churn over multiple sub-checkpoints.
   */
  diplomacy: {
    /** Fraction of non-homeworld habitable planets seeded as
     *  populated neutrals. New-game UI overrides via
     *  `gameSettings.neutralPlanetDensity`; this is the fall-through
     *  default when no UI value is set. Range 0..0.6. */
    neutralPlanetDensity: number;
    /** Per-planet starting pop for seeded neutrals, expressed as a
     *  fraction of biome cap. Rolled per planet via `state.rng`. */
    neutralStartingPopMin: number;
    neutralStartingPopMax: number;
    /** Per-biome pre-built defensive structure counts. A biome with
     *  zero counts across all categories is treated as ineligible
     *  for neutral seeding by `scenario.ts`. Default eligible set:
     *  gaia / temperate / ocean / arctic. */
    defensiveStructures: Readonly<
      Record<
        BiomeKind,
        { bunkers: number; surfaceBatteries: number; embassy: number }
      >
    >;
    /** Per-outcome Influence thresholds (`DIPLOMACY.md §6`). Empire
     *  invokes the corresponding outcome once its per-neutral pool
     *  meets or exceeds the threshold. Cumulative breakpoints — an
     *  empire at 500 has Trade and Federation unlocked, not Vassal /
     *  Annex. */
    thresholds: {
      trade: number;
      federation: number;
      vassal: number;
      annex: number;
    };
    /** Vassal tribute as a fraction of the planet's per-tick
     *  resource production (Credits / Minerals / Research / Fuel)
     *  routed to the suzerain. Population stays on the planet. */
    vassalTributeFraction: number;
    /** Per-tick Credits + Minerals each Trade-arrangement empire
     *  receives from a trading neutral. Placeholder values pending
     *  v3M21.3 implementation + retune. */
    trade: {
      creditsPerTick: number;
      mineralsPerTick: number;
    };
  };
}

/**
 * MFix3.2 — Habitability data loaded from `docs/Habitability.xlsx` via the
 * convert-xlsx pipeline. Keys use the xlsx labels as authored ('Tropical',
 * 'Oceanic', 'Frozen', 'Toxic', 'Radioactive', etc.); the runtime BiomeKind
 * has fewer entries (no 'Toxic' or 'Radioactive' in v1) so a runtime-side
 * label mapping (in `balance-habitability.ts`) translates BiomeKind to
 * xlsx label before lookup. Toxic / Radioactive entries in this data are
 * preserved for future biome additions but never queried in v1.
 */
export interface HabitabilityBand {
  scoreMin: number;
  scoreMax: number;
  perTickMultiplier: number;
  productionEfficiency: number | null;
}

export interface HabitabilityTechBonusRow {
  techName: string;
  techType: string;
  bonuses: Record<string, Record<string, number>>;
}

export interface HabitabilityData {
  raceAffinity: Record<string, Record<string, number>>;
  baseInherent: Record<string, number>;
  techBonus: HabitabilityTechBonusRow[];
  bands: {
    hostile: HabitabilityBand;
    neutral: HabitabilityBand;
    hospitable: HabitabilityBand;
  };
  sizeModulation: Record<string, number>;
}

/**
 * v3M20 — name pools loaded from `docs/Names.xlsx`. Used at scenario
 * init to assign flavour names to systems and planets in place of the
 * `Sys-A` / `Planet-${id}` placeholder strings. Consumed once at galaxy
 * generation and never read again — lives on `LoadedGameData` only,
 * not on `GameState` (the per-system / per-planet `name` fields persist
 * the picked names directly).
 *
 * Pools are deliberately flat string arrays — no per-race / per-biome
 * partitioning in v3M20.1. Future v3M20.x sub-checkpoints can extend
 * the schema if narrative warrants (e.g. per-race system flavour).
 *
 * Pool exhaustion handling: scenario gen samples without replacement
 * via a per-galaxy `used` Set. When the pool is exhausted, additional
 * picks fall back to `<base> II`, `<base> III` etc. on collision.
 */
export interface NamesData {
  systems: readonly string[];
  planets: readonly string[];
}

/**
 * Planet size — integer in [5, 20] (MFix1.4). Drives:
 *   - `populationCap = size * 10` (size 15 home world → cap 150)
 *   - `maxStructures = size` (PLANETS §2.1, "maxStructures = Size × 1")
 *   - Sim radius (linear interp 0.03..0.07 in [5, 20])
 *   - Screen render radius (linear interp 14..28 px in [5, 20])
 *
 * Pre-MFix1.4 was a 3-value enum ('small' | 'medium' | 'large');
 * SAVE_FORMAT_VERSION bumped to 2 to invalidate old saves rather
 * than migrate (saves are cheap to recreate).
 */
export type PlanetSize = number;

export type WeaponCategory =
  | 'Energy Pulse'
  | 'Energy Beam'
  | 'Kinetic Burst'
  | 'Kinetic Railgun'
  | 'Concussion Missile'
  | 'Concussion Torpedo'
  | 'EMP';

export type ShipClass = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Environmental entities that populate tactical in-system space.
 *
 * - **asteroid-field / gas-giant** (M14): site-validation targets for
 *   Constructor-built resource extractors.
 * - **nebula / black-hole / ion-cloud / radiation-belt** (MFix4.2,
 *   2026-04-28): cosmetic-with-hook-ready entities. v1 has no gameplay
 *   effect for these (PLANETS.md lists the design-intended hooks —
 *   nebula likely suppresses scan range, black hole gravity-wells
 *   movement, ion cloud / radiation belt damage-over-time or
 *   traversal-tech-required). Wired into the data model now so
 *   future milestones can attach effects without a save-format
 *   bump or scenario regen.
 */
export type EnvironmentKind =
  | 'asteroid-field'
  | 'gas-giant'
  | 'nebula'
  | 'black-hole'
  | 'ion-cloud'
  | 'radiation-belt';

export interface EnvironmentEntity {
  id: EntityId;
  systemId: SystemId;
  /** In-system position (local 0..1 coords). */
  sx: number;
  sy: number;
  /** Local-coord radius — Constructor is "inside" when within this distance. */
  radius: number;
  kind: EnvironmentKind;
}

/**
 * Planet-side structure kinds (STRUCTURES §2). Space-based structures
 * (Orbital Defence Platform ring, Weapons Platforms, Repair Yards, Sensor
 * Buoys, etc.) are classified as "stationary ships" per STRUCTURES §3 and
 * live in `GameState.ships`, not here.
 *
 * Sensor Buoy (PLANETS §2.9 deep-space variant) defers to a separate v3
 * milestone — different deployment story (Constructor-built space
 * structure rather than planet structure), no design lock yet.
 */
export type StructureKind =
  | 'shipyard'
  | 'cloning-center'
  | 'trade-center'
  | 'processing-plant'
  | 'research-lab'
  | 'refinery'
  | 'bank'
  | 'warehouse'
  | 'fuel-dump'
  | 'embassy'
  // v2M6 planetary defences (STRUCTURES §2.2). Shields + Bunkers cap at
  // L5; surface batteries follow the regular L1-L6 ladder.
  | 'planetary-shields'
  | 'underground-bunkers'
  | 'energy-surface-battery'
  | 'kinetic-surface-battery'
  // v3M1 sensor structure (STRUCTURES §2.3). Unique-per-planet, scales
  // the host planet's scan range by an additive per-level bonus on top
  // of the MFix1.5 flat-constant baseline.
  | 'long-range-sensor-array'
  // v3M2.2 Orbital Defence Platform (STRUCTURES §3.4). Planet-side
  // command structure that on activation spawns 6 ring platforms in
  // orbit, each a stand-alone ShipInstance armed with the empire's
  // best-researched Concussion missile. Levels 1-6 scale the chassis
  // stats of all six platforms simultaneously. Ring deployment +
  // rebuild queue land in v3M2.2b/c; the scaffold here just adds the
  // kind to the catalogue.
  | 'orbital-defence-platform';

/**
 * A single tech entry from Tech_Tree.xlsx. Identified by `techName` (the
 * canonical string used for `empire.researchedTechs`, weapon/ability
 * lookups, and `Command.setResearchTarget.techName`). Research completion
 * just means adding `techName` to the empire's researched set; effects
 * are read through downstream helpers that key off the category + level.
 *
 * `race` is 'All' for universal techs or a specific RaceId for race-
 * locked ones. `prerequisites` is an array of techNames that must be
 * researched before this one can be targeted.
 */
export interface TechDef {
  techName: string;
  category: string;
  level: number | null;
  race: string;
  prerequisites: readonly string[];
  researchCost: number;
  researchTimeTicks: number | null;
  effect: string;
  unlocks: string | null;
  /**
   * MFix7.2 — numeric value parsed from `effect` at xlsx load time.
   * Categories that have a multiplier-style effect (Hull, Hyperdrive,
   * Sublight Speed, Shield-max-multiplier) populate `parsedMultiplier`
   * with the ×N.M factor. Categories without a multiplier effect leave
   * it null. Lets `effectiveMaxHull` / `hyperdriveCostMultiplier` /
   * etc. read straight from the spreadsheet rather than computing a
   * formula from Mk level.
   */
  parsedMultiplier: number | null;
  /**
   * MFix7.2 — additive numeric value parsed from `effect`. Used by
   * categories with additive bonuses: Armor (% damage reduction → 0..1
   * fraction), Stealth + Sensors (flat empire-wide bonus), EMP
   * Protection (tick reduction). Null for categories without an
   * additive effect.
   */
  parsedAdditive: number | null;
  /**
   * MFix7.2 — Shield regen rate (% of max shields per tick) parsed
   * from `effect`. Stored as a fraction (0.001 = 0.1%/tick). Null
   * for non-Shield categories.
   */
  parsedRegenPercent: number | null;
}

/**
 * A single weapon variant from Tech_Tree.xlsx — one Mk of one subtype (e.g.
 * "Pulse Laser Mk 1"). Ship templates reference weapons generically by slot
 * (e.g. "Energy Pulse Weapon"); the combat pass resolves the slot to the
 * highest Mk the owner empire has researched (falling back to Mk 1 which is
 * always-available with researchCost = 0).
 *
 * `damageType` is the top-level bias used by the damage chain (Energy gets
 * +shield/−hull, Kinetic −shield/+hull, Concussion neutral — MECHANICS §3.1).
 * `magazine` is finite only for Concussion weapons; null means unlimited.
 * `blastRadius` gates splash for Concussion; null = no splash (used for the
 * M11 scaffold — splash resolution itself is deferred).
 */
export interface WeaponDef {
  name: string;
  category: 'Energy' | 'Kinetic' | 'Concussion' | 'Special';
  subType: 'Pulse' | 'Beam' | 'Burst' | 'Railgun' | 'Missile' | 'Torpedo' | 'EMP';
  mk: number;
  rateOfFire: number;
  range: number;
  damageType: 'Energy' | 'Kinetic' | 'Concussion' | 'EMP';
  damage: number;
  accuracy: number;
  magazine: number | null;
  blastRadius: number | null;
}

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------

export interface ResourcePool {
  credits: number;
  research: number;
  minerals: number;
  population: number;
  diplomatic: number;
  fuel: number;
}

/**
 * Per-pop per-tick production rates (ECONOMY §1.2, v1).
 *
 * v3M10.1: migrated to state.balanceData.economy.popOutputPerTick.
 */
export function popOutputPerTick(balance: BalanceData) {
  return balance.economy.popOutputPerTick;
}

// MFix3.2: removed `BASE_POP_GROWTH_PER_TICK` constant — population
// dynamics are now Habitability-driven per band. The hospitable-band
// per-tick multiplier (currently 0.0005, matching the MFix1.3 value)
// is authored in `docs/Habitability.xlsx` Bands sheet, loaded via the
// convert-xlsx pipeline into `state.habitability.bands.hospitable`,
// and applied by `popDeltaPerTick` in `balance-habitability.ts`.

// ---------------------------------------------------------------------------
// Planets
// ---------------------------------------------------------------------------

export interface Planet {
  id: PlanetId;
  systemId: SystemId;
  name: string;
  biome: BiomeKind;
  size: PlanetSize;
  populationCap: number; // = 100 * sizeMultiplier (PLANETS §2.1)
  currentPopulation: number;
  ownerEmpireId: EmpireId | null; // null = uninhabited
  /** In-system position (GALAXY §3.5.1), local 0..1 coords with star at (0.5, 0.5). */
  systemX: number;
  systemY: number;
  /** Structures on this planet (ids into GameState.structures when added). */
  structureIds: EntityId[];
  /**
   * Ship-production queue for this planet (M10). Index 0 is the active build
   * — advance-shipyard.ts ticks its `buildTicksRemaining` each tick; on
   * reaching 0 the ship spawns and the entry shifts out. Subsequent entries
   * wait in order. Resources (credits/minerals/pop) are deducted UP FRONT
   * at queue time by applyBuildShip so an empty queue can't silently
   * overdraw the empire later. Empty array when the planet has nothing
   * building; array is always present to keep the per-tick pass branch-free.
   */
  shipBuildQueue: ShipBuildOrder[];
  /** Terraforming state: null = unmodified; otherwise target biome + progress. */
  terraforming: {
    targetBiome: BiomeKind;
    ticksRemaining: number;
    fuelCost: number;
    mineralsCost: number;
  } | null;
  /**
   * Loyalty 0–100 (PLANETS §2.7 / STRUCTURES §8). Initialised to 100 on
   * colonisation. Drops only from enemy bombardment per the v2M5 formula:
   * once every 10 ticks, `ceil(2 × kills_in_window / pop_at_window_start
   * × 100)`. Regenerates +1 per 10 ticks while not under bombardment,
   * capped at 100. At 0 the planet flips to `lastBombardingEmpireId`.
   * Visible only to the planet's owner.
   */
  loyalty: number;
  /**
   * Pop kills accumulated since the start of the current 10-tick batch
   * window. Drained at each batch boundary into the Loyalty drop calc.
   * 0 when no bombardment is active.
   */
  bombardKillsAccumulator: number;
  /**
   * Pop snapshot at the start of the current 10-tick batch window. The
   * denominator in the Loyalty drop formula. null when no bombardment is
   * active; captured on the first tick that delivers bombardment damage in
   * a fresh window, reset at each batch boundary.
   */
  bombardPopBaseline: number | null;
  /**
   * Last sim tick at which any bombardment damage landed on this planet.
   * Drives the regen gate: `currentTick - lastBombardedTick >= 10` => regen
   * resumes. null while planet has never been bombarded (or after regen
   * has fully closed out the batch state).
   */
  lastBombardedTick: number | null;
  /**
   * Empire whose ship most recently fired bombardment on this planet
   * within the active batch window. On Loyalty 0, planet flips to this
   * empire (killing-blow rule under 10-tick batching). null while no
   * bombardment is active.
   */
  lastBombardingEmpireId: EmpireId | null;
  /**
   * v3M21.1 — Per-empire diplomatic Influence accumulated on this
   * planet (`DIPLOMACY.md §5`). Each empire that has spent Diplomatic
   * resource targeted at this planet appears as a key; missing keys
   * mean zero Influence. Crossing per-outcome thresholds
   * (Trade 200 / Federation 400 / Vassal 600 / Annex 800 by default,
   * tunable in `state.balanceData.diplomacy`) unlocks the matching
   * outcome for that empire to invoke. One-way ratchet — no decay;
   * only Annex resolution collapses pools to 0.
   *
   * Empty Map on owned planets and on freshly-seeded neutrals — only
   * gets entries once an empire spends. Owned planets carry the field
   * for shape uniformity but never accumulate (Diplomatic spend is
   * gated to neutral targets in `setDiplomaticFocus`).
   */
  influence: Map<EmpireId, number>;
  /**
   * v3M21.1 — Empire this neutral is federated with, or null. While
   * non-null, non-federated empires' Influence on this planet caps at
   * `federationThreshold` (locks rivals out of Federation / Vassal /
   * Annex; Trade still allowed below the lock). Cleared on Annex.
   * `DIPLOMACY.md §6.2`.
   */
  federatedWith: EmpireId | null;
  /**
   * v3M21.1 — Empires currently in a Trade arrangement with this
   * neutral. Multiple empires can trade simultaneously (Trade is
   * non-exclusive — `DIPLOMACY.md §6.3`). Per-tick income flows from
   * planet → each empire in this list per the Balance.xlsx
   * `tradeCreditsPerTick` / `tradeMineralsPerTick` tunables. Cleared
   * on Annex.
   */
  tradingWith: EmpireId[];
  /**
   * v3M21.1 — Empire this neutral is vassal to, or null. While
   * non-null, the planet routes `vassalTributeFraction` (default
   * 0.25) of its Credits / Minerals / Research / Fuel production to
   * the suzerain each tick (`DIPLOMACY.md §6.4`). Population stays on
   * the planet. Vassalage flips if a different empire reaches
   * `vassalThreshold` and invokes Vassal (only possible if planet is
   * not federated). Cleared on Annex.
   */
  vassalOf: EmpireId | null;
}

/**
 * A ship-build order sitting in a planet's `shipBuildQueue` (M10).
 *
 * The order is pre-funded: credits/minerals/pop were deducted when
 * applyBuildShip queued it, so the advance pass only needs to tick
 * `buildTicksRemaining` and spawn on zero. `buildTicksTotal` is a snapshot
 * of the original duration so the UI can show a progress bar.
 *
 * `chassis` is the empire race's chassis name (not the `RACE-XX:Chassis`
 * composite key); the applier/advance pass re-derives the full template
 * key from `empire.raceId`.
 */
export interface ShipBuildOrder {
  chassis: string;
  buildTicksRemaining: number;
  buildTicksTotal: number;
}

// ---------------------------------------------------------------------------
// Galaxy
// ---------------------------------------------------------------------------

export interface StarSystem {
  id: SystemId;
  name: string;
  /** Canonical 2D galactic position (normalised coordinates 0..1). */
  x: number;
  y: number;
  planetIds: PlanetId[];
  /** Jump points belonging to this system (GALAXY §3.5.1). 3–6 per system. */
  jumpPointIds: JumpPointId[];
}

/**
 * A jump point is a system's entry/exit node for interstellar travel. Each
 * system has 3–6 placed on an outer ring at radius ~0.48 from the system
 * centre (local 0..1 coords). Linkages to another system's jump point are
 * null at the skeleton stage and will be wired by a later milestone.
 */
export interface JumpPoint {
  id: JumpPointId;
  systemId: SystemId;
  /** In-system position (local 0..1 coords). */
  x: number;
  y: number;
  /** Destination system this jump point connects to — null until linked. */
  linkedSystemId: SystemId | null;
  /** Destination jump point — null until linked. */
  linkedJumpPointId: JumpPointId | null;
}

/**
 * v3M18 — explicit player-authored Warp Gate connection. Each link pairs two
 * Warp Gate ShipInstances (always same empire) into a virtual hyperlane
 * between their two systems. Per-gate cardinality is 1: a ShipInstance is
 * referenced by at most one live or building link.
 *
 * Lifecycle: a link is created in status `'building'` with progressTicks 0
 * and targetTicks 600 (locked). `advanceWarpGateLinks` increments
 * progressTicks each tick; on hitting targetTicks the status flips to
 * `'live'`. Only `'live'` links provide travel. Disconnect drops the link
 * regardless of status, instantly + free. On either gate's destruction the
 * link is severed and removed from `state.warpGateLinks`.
 *
 * Travel via a live link uses `galaxyDistance(systemA, systemB) /
 * ship.Hyperdrive` — same formula as a standard jump. The strategic value
 * of the connection is authored topology, not multiplicative speedup.
 */
export interface WarpGateLink {
  id: WarpGateLinkId;
  ownerEmpireId: EmpireId;
  /** ShipInstanceId of the first Warp Gate. Symmetric with gateBId. */
  gateAId: ShipInstanceId;
  /** ShipInstanceId of the second Warp Gate. */
  gateBId: ShipInstanceId;
  /** Cached system ids for the two gates — denormalised for fast lookup. */
  systemAId: SystemId;
  systemBId: SystemId;
  status: 'building' | 'live';
  /** 0..targetTicks. When status === 'live', equals targetTicks. */
  progressTicks: number;
  /** 600 in v3M18; lifted to a tunable later if balance demands. */
  targetTicks: number;
}

// ---------------------------------------------------------------------------
// Ships & fleets
// ---------------------------------------------------------------------------

/**
 * Ship *template* — static per-chassis stats read from ships.json.
 * One template is shared across all instances of that chassis.
 */
export interface ShipTemplate {
  chassis: string;
  classLevel: ShipClass;
  raceId: RaceId;
  role: string;
  hull: number;
  armor: number;
  shields: number;
  empProtection: number;
  sublightSpeed: number;
  dodge: number;
  visionRange: number;
  scanRange: number;
  detectionBase: number;
  stealthBase: number;
  energyCapacity: number | null;
  energyRegen: number | null;
  hyperdriveFuelUse: number;
  /**
   * Ticks to spool up hyperdrive before jumping. Per-ship: a fast Scout jumps
   * quicker than a heavy Cruiser even if they leave together. Authored in
   * ships.xlsx; loader defaults to 100 (~10s at 10 Hz) when missing.
   */
  jumpDurationTicks: number;
  weaponLoadout: Array<{ weapon: string; count: number }>;
  embarks: Array<{ chassis: string; count: number }>;
  popBuildCost: number;
  creditsBuildCost: number;
  mineralsBuildCost: number;
  upkeep: number;
  canCloak: boolean;
  specialAbilities: string[];
  /**
   * MFix8.5 — Aura name this chassis emits, or null/undefined. References
   * a row in `state.auras` (loaded from the Auras sheet on Ships.xlsx).
   * The aura helper scans emitter ships in the same system within
   * `range`; matching emitters' bonuses stack multiplicatively
   * (shieldMult / shieldRegenMult / damageMult / speedMult) or
   * additively (stealthBonus / detectionBonus).
   *
   * Optional rather than required so existing inline ShipTemplate
   * literals in tests don't need a sweep. Mirrors the `chassisLevel?`
   * precedent from v3M2.2.
   */
  aura?: string | null;
}

/**
 * MFix8.5 — Aura definition pulled from `Auras` sheet on Ships.xlsx.
 * Each row defines one aura, identified by `name`. Emitter ships
 * reference an aura by name via `ShipTemplate.aura`.
 *
 * Bonus columns:
 *   - `shieldMult` / `shieldRegenMult` / `damageMult` / `speedMult` —
 *     multiplicative; 1.0 = no effect, 1.5 = +50%. Author-side 0 / blank
 *     normalises to 1.0 in convert-xlsx.
 *   - `stealthBonus` / `detectionBonus` — additive flat values added on
 *     top of the consumer's existing stealth / detection.
 *
 * Auras are friendly-only (same `ownerEmpireId` as the emitter), in-
 * system (mid-jump emitters can't grant), and apply to allies inside
 * `range` (in 0..1 in-coord units after the MFix8.2 unit alignment).
 * Emitters are themselves allies of themselves so the emitter receives
 * its own buff.
 */
export interface AuraDef {
  name: string;
  range: number;
  shieldMult: number;
  shieldRegenMult: number;
  damageMult: number;
  speedMult: number;
  stealthBonus: number;
  detectionBonus: number;
}

/**
 * Ship *instance* — a single live ship. Carries mutable combat state (current
 * hull, shields, energy, disablement timers), sublight position, an optional
 * move order, and jump state. Ships are the unit of simulation in v1: each
 * ship has its own systemId and jumps independently. Fleets as a simulation
 * concept are intentionally absent — they will return later as a player-authored
 * UI grouping (control groups).
 *
 * Sublight coordinates (sx, sy) are in local in-system space, same 0..1 frame
 * as Planet.systemX/systemY and JumpPoint.x/y. While mid-jump the values are
 * retained (as the last known position in the origin system) but not used —
 * the galaxy-view renderer interpolates along the hyperlane instead.
 */
export interface ShipInstance {
  id: ShipInstanceId;
  chassis: string;
  ownerEmpireId: EmpireId;
  currentHull: number;
  currentShields: number;
  currentEnergy: number;
  disabledTicks: number; // EMP disablement countdown
  cloaked: boolean;
  /** Current system; null while mid-jump. */
  systemId: SystemId | null;
  /** In-system sublight position (local 0..1 coords). Unused while mid-jump. */
  sx: number;
  sy: number;
  /**
   * Active sublight move order, or null if the ship is idle. The ship steps
   * toward (targetX, targetY) at sublightSpeed (per-tick, from template) each
   * advanceTick until it arrives, at which point the order clears.
   */
  moveOrder: { targetX: number; targetY: number } | null;
  /**
   * Origin system while mid-jump; null when not jumping. Captured at jump
   * start so the galaxy renderer can interpolate the ship's screen position
   * between origin and destination instead of teleporting it.
   */
  jumpOriginSystemId: SystemId | null;
  /** Target system while mid-jump; null if not jumping. */
  jumpTargetSystemId: SystemId | null;
  /**
   * Destination jump point for the current in-progress jump; null when not
   * jumping. Captured at departure so the sim can drop the ship at the linked
   * JP's (x, y) on arrival without having to re-derive the pairing.
   */
  jumpTargetJumpPointId: JumpPointId | null;
  /**
   * v3M18 — destination Warp Gate ShipInstance for the current
   * in-progress warp-link jump; null for standard JP-based jumps and
   * for stationary ships. Mutually exclusive with
   * `jumpTargetJumpPointId` — at most one is set per jump. Captured at
   * departure so the sim can land the ship at the partner gate's
   * (sx, sy) on arrival without re-scanning warpGateLinks. If the
   * partner gate is destroyed mid-jump (severing the link), arrival
   * falls back to leaving sx/sy as-is.
   */
  jumpTargetGateId: ShipInstanceId | null;
  /**
   * v3M18.5 — pending Warp Gate link to fire on sublight arrival. Set
   * by `applyJumpShipsViaWarpGate` alongside a `moveOrder` aimed at
   * the source gate's (sx, sy); cleared either when the warp jump
   * actually fires (in `tryStartPendingWarpGateJump`) or when a new
   * order overrides this one (move/jump/cloak/build clear it). Mirrors
   * `pendingJumpPointId` for JP-based `moveAndJumpShips` — fuel is
   * checked at fire time, not at issue, so players can queue a warp
   * jump while still mining the cost.
   */
  pendingWarpGateLinkId: WarpGateLinkId | null;
  /**
   * Ticks remaining before this ship's jump resolves. Stored per-ship so a
   * fast Scout and a slow Cruiser that leave together can arrive separately.
   */
  jumpTicksRemaining: number;
  /**
   * Total duration of the current in-progress jump (snapshotted from template
   * at jump start). Lets the renderer compute t = 1 - remaining/total without
   * re-looking-up the template during interpolation.
   */
  jumpDurationTotal: number;
  /**
   * Tick index on which this ship last fired any weapon, or null if it never
   * has. Consumed by effectiveStealth to drop Stealth to 0 for a short
   * window after firing (MECHANICS §2.1). Always null in v1 until combat is
   * wired; carrying the field now keeps the detection pass branchless.
   */
  lastFiredTick: number | null;
  /**
   * Last ship targeted by this attacker — set by `fireSalvo` in
   * `advance-combat` when ship-vs-ship combat fires (v2M7.4a). Null when
   * the ship hasn't fired or fired at a non-ship target (planet
   * bombardment doesn't set this — bombardOrder.targetPlanetId is the
   * authoritative target there). Render layer reads this to draw a
   * tracer line from this ship to its target each frame the fire
   * happened.
   *
   * Forward-compat: future AI / UI ("who's shooting at me?") may also
   * read this. Save/load round-trips it via the standard {@link
   * deserializeGameState} `...spread` path — it's a plain number.
   */
  lastFiredTargetShipId: ShipInstanceId | null;
  /**
   * If non-null, this ship is queued to jump through the referenced jump
   * point AS SOON AS it reaches its current moveOrder target. The RMB-on-JP
   * UX sets moveOrder to the JP's sublight position + pendingJumpPointId to
   * the JP's id; advanceShipMovement then auto-starts the jump on arrival
   * (M8.2). Cleared by applyMoveShips so a plain move order cancels a queued
   * jump. Always null when the ship is already mid-jump (systemId === null).
   */
  pendingJumpPointId: JumpPointId | null;
  /**
   * MFix3.4 — pending colonisation target. Set by `applyMoveAndColonise`
   * (RMB-on-uninhabited-planet UX) alongside a sublight `moveOrder` aimed
   * at the planet's centre. When the ship arrives (moveOrder cleared),
   * `advancePendingColonisations` invokes `applyColonise` to commit the
   * landing — including the full validation chain (Settlement tech, range,
   * uninhabited gate). Cleared either on success OR on arrival-with-failure
   * so a stale pending order does not retry across ticks. Mirrors the
   * `pendingJumpPointId` pattern from M8.2's RMB-on-JP gesture.
   */
  pendingColoniseTargetId: PlanetId | null;
  /**
   * Active construction order on this ship (M14). Only Constructor-class
   * ships carry a non-null value here. The applier sets both this field
   * and a `moveOrder` to the build site at queue time; the Constructor
   * travels there at sublight. advance-construction ticks `ticksRemaining`
   * ONLY once `moveOrder === null` (arrival). On reaching 0 the target
   * structure spawns at `(sx, sy)` — the build location captured at
   * queue time, NOT the Constructor's current position (they should match
   * but the captured coords are authoritative).
   *
   * New player move / jump / cloak commands clear this on the Constructor
   * (explicit abandon) — resources are NOT refunded; partial build is
   * sunk cost per STRUCTURES §3 "if destroyed → partial build also
   * destroyed, no refund".
   */
  constructionOrder: {
    structureKind: string;
    /** Build-site target (local 0..1 in-system coords). Captured at queue time. */
    sx: number;
    sy: number;
    ticksRemaining: number;
    ticksTotal: number;
  } | null;
  /**
   * Active bombardment order on this ship (v2M5, PLANETS §2.7).
   * Set by the BombardPlanet command (RMB on enemy/neutral planet with
   * own ship(s) selected). The bombardment advance pass uses this to
   * pick a target each tick; while active the ship fires unspent weapon
   * slots at the planet whenever in range. Mutually exclusive with
   * `constructionOrder` and cleared by any new player move / jump / cloak
   * / build command — same exclusion pattern as constructionOrder.
   * Cleared automatically when the target planet flips owner or
   * depopulates so ships don't pin to a vanished target.
   */
  bombardOrder: {
    targetPlanetId: PlanetId;
  } | null;
  /**
   * Per-weapon-slot runtime state, parallel to `template.weaponLoadout`. One
   * entry per loadout slot (where a slot can represent 1+ identical weapons —
   * see `count` on WeaponLoadoutEntry; all turrets in a slot share the slot's
   * cooldown and magazine and fire as a salvo on each ready tick). Populated
   * at ship spawn from the template and maintained by the combat pass.
   *
   * `cooldownTicks` counts down at 1/tick by advance-combat; a slot is ready
   * to fire when it reaches 0. On fire the slot resets to ceil(1/rateOfFire)
   * ticks. `ammoRemaining` is only meaningful for Concussion weapons (weapons
   * with a finite `magazine`); non-Concussion slots carry Infinity and the
   * combat pass never decrements them.
   */
  weaponSlots: WeaponSlotState[];
  /**
   * v3M2.2 — chassis-level override for ring platforms spawned by an
   * Orbital Defence Platform. Equals the parent ODP structure's level
   * (1..6) at platform spawn time and is updated by the advance-odp
   * pass when the ODP upgrades. `effectiveMaxHull` and the scanner
   * collector apply a per-level multiplier when this is set.
   *
   * `undefined` for every regular ship — the helpers fall back to
   * template stats. Optional rather than required so existing
   * ShipInstance literals across tests don't need a sweep.
   */
  chassisLevel?: number;
  /**
   * v3M2.3 — remaining mine count for Minefield chassis. Starts at
   * `MINE_COUNT_INITIAL` (20) on spawn; decremented each time a mine
   * triggers; despawn fires when it reaches 0. Self Replicating Mines
   * tech regenerates +1/tick up to `MINE_COUNT_CAP`. `undefined` for
   * every non-Minefield ship.
   */
  mineCount?: number;
  /**
   * v3M2.3 — Cloaked Mines reveal flag. When the empire holds the
   * `Minefields - Cloaked Mines` tech (RACE-CC), the minefield is
   * fully invisible to enemies until the first mine triggers, after
   * which it becomes permanently visible (this flag flips true).
   * `undefined` for every non-Minefield ship.
   */
  mineCloakRevealed?: boolean;
  /**
   * v3M2.3 — per-target trigger throttle. Records the sim tick at
   * which each ship-id last triggered a mine on this minefield. The
   * throttle prevents a ship sitting in the field from chewing through
   * mines instantly: a triggered ship is immune for the next
   * `MINE_TRIGGER_COOLDOWN_TICKS` ticks. JSON-safe entry-array form
   * (deserialised back into a Map at runtime). Sorted by ship-id
   * ascending for determinism. `undefined` for every non-Minefield
   * ship.
   */
  mineLastTriggerTickByShipId?: Array<[ShipInstanceId, number]>;
  /**
   * v3M17.1 — Carrier embarked-squadron count (0..4). Set on Carrier /
   * Heavy Carrier / Stealth Carrier ShipInstances at build time;
   * `undefined` for every non-carrier. Decrements on launch, increments
   * on recall (capped at 4 in the recall path). Combined with
   * `rebuildProgressFighters` to drive the per-tick squadron rebuild
   * cadence in `advance-carrier-squadrons.ts` (v3M17.2). See SHIPS.md
   * §5 + docs/v3M17-design.md §2.
   */
  embarkedSquadronCount?: number;
  /**
   * v3M17.1 — Per-fighter rebuild accumulator (0..5). Increments by 1
   * every 100 ticks (preserves the 2026-04-21 SHIPS.md §5 rebuild
   * lock); on hitting 5, spawns a docked squadron
   * (`embarkedSquadronCount += 1`) and resets to 0. Halts when
   * `embarkedSquadronCount === 4`. `undefined` for every non-carrier.
   * v3M17.2 wires the per-tick increment.
   */
  rebuildProgressFighters?: number;
  /**
   * v3M17.1 — Per-carrier Attack Behaviour setting. Governs auto-launch
   * trigger range:
   *   - 'none'           → never auto-launch; manual launch only.
   *   - 'visual-range'   → launch when any visible enemy enters
   *                        carrier's template `visionRange`.
   *   - 'scan-range'     → launch when any scan-detected enemy enters
   *                        carrier's template `scanRange`.
   *
   * Default 'visual-range' on construction. `undefined` for every non-
   * carrier. Settable from the carrier's detail panel (v3M17.4).
   * v3M17.2 wires the auto-launch pass.
   */
  carrierAttackBehaviour?: 'none' | 'visual-range' | 'scan-range';
  /**
   * v3M17.1 — Per-carrier Defense Behaviour setting. Governs auto-recall
   * trigger:
   *   - 'return-at-low'  → squadron recalls itself when its
   *                        `liveFighterCount` drops to 2.
   *   - 'no-surrender'   → never auto-recall; squadron fights to the
   *                        death.
   *
   * Default 'return-at-low' on construction. `undefined` for every
   * non-carrier. Settable from the carrier's detail panel (v3M17.4).
   * v3M17.2 wires the auto-recall pass.
   */
  carrierDefenseBehaviour?: 'return-at-low' | 'no-surrender';
  /**
   * v3M17.1 — Squadron's surviving fighter count (0..5). Set on
   * squadron-chassis spawn (always 5 at launch); `undefined` for every
   * non-squadron ship. Drives the variable-count HP pool in v3M17.3
   * (squadron hull pool = `liveFighterCount × parent fighter.hull`).
   * Squadron despawns when this reaches 0.
   */
  liveFighterCount?: number;
  /**
   * v3M17.1 — Squadron's parent carrier ShipInstance id. Set on
   * squadron spawn; `undefined` for every non-squadron ship. Used by
   * the recall path (squadron credits surviving fighters back to the
   * carrier's `rebuildProgressFighters`), the carrier-destruction
   * sever path (v3M17.5), and the carrier-jump auto-recall pre-emption
   * (v3M17.5).
   */
  parentCarrierShipId?: ShipInstanceId;

  // -------------------------------------------------------------------------
  // v3M22 — Player ship commands
  // -------------------------------------------------------------------------
  /**
   * v3M22 — high-level command mode governing what the ship does on a
   * continuing basis (vs. the existing single-shot orders like
   * `moveOrder` / `bombardOrder`). The per-tick `advance-ship-commands`
   * pass interprets each kind and writes the appropriate `moveOrder`
   * so the existing sublight loop handles the actual stepping.
   *
   * Kinds (`MECHANICS.md §5`):
   *   - `'idle'`     — default. Player single-shot orders apply as before.
   *   - `'patrol'`   — cycle the `waypoints` chain forever (§5.2). No-op
   *                    if `waypoints.length === 0`.
   *   - `'explore'`  — auto-jump to the least-scouted reachable system
   *                    until all reachable systems are scouted, then
   *                    auto-clear to `'idle'` (§5.3).
   *   - `'guard'`    — orbit / follow the entity at `guardTargetId`
   *                    (kind disambiguated by `guardTargetKind`; §5.4).
   *
   * Cancel-on-jump (§5.5): `commandKind` resets to `'idle'`, `waypoints`
   * clears to `[]`, guard target fields clear to `null`. Re-acquire-on-
   * arrival is intentionally out of scope for v3M22.
   *
   * AI does not use these commands in v3M22 (§5.6). AI ship movement
   * stays governed by the existing dispatch passes.
   */
  commandKind: 'idle' | 'patrol' | 'explore' | 'guard';
  /**
   * v3M22 — per-ship waypoint queue. Populated via the player UX:
   * plain RMB at a destination replaces the queue with `[point]`;
   * Shift+RMB appends. Coordinates are in-system (0..1 frame).
   * Cleared on jump. `'patrol'` cycles the chain; other command kinds
   * ignore it. Cross-system chains are out of scope for v3M22.
   */
  waypoints: Array<{ targetX: number; targetY: number }>;
  /**
   * v3M22 — disambiguates `guardTargetId` between a planet (orbits at
   * `planetSimRadius + 0.04`) and a friendly ship (follows at ~0.05
   * leash). `null` outside `'guard'` mode. The pair `(guardTargetKind,
   * guardTargetId)` is set together by the `setShipGuard` Command and
   * cleared together on jump or when the player reverts to another
   * command.
   */
  guardTargetKind: 'planet' | 'ship' | null;
  /**
   * v3M22 — id of the entity being guarded. Interpreted as a `PlanetId`
   * when `guardTargetKind === 'planet'` and a `ShipInstanceId` when
   * `guardTargetKind === 'ship'`. `null` outside `'guard'` mode.
   */
  guardTargetId: EntityId | null;
}

/**
 * Per-weapon-slot mutable runtime state, one per `ShipTemplate.weaponLoadout`
 * entry. Array index aligns 1:1 with the template's slot order — do not
 * reorder without remapping existing ShipInstance rows.
 */
export interface WeaponSlotState {
  /** Ticks until the slot is ready to fire again. Zero = ready now. */
  cooldownTicks: number;
  /** Shots left in the magazine. Infinity for unlimited-magazine weapons. */
  ammoRemaining: number;
}

// ---------------------------------------------------------------------------
// Planet-side structures
// ---------------------------------------------------------------------------

/**
 * A planet-side structure instance (STRUCTURES §2, §4).
 *
 * Lifecycle:
 *   - Initial build: created with `level = 0`, `buildTicksRemaining =
 *     BUILD_TICKS_FOR_LEVEL[0]`. `advanceStructures` ticks down;
 *     on reaching 0 the structure activates by incrementing `level` to 1.
 *   - Active: `level` in 1..6. Generates effects (economy multipliers,
 *     Shipyard class gate, Embassy Diplomatic influx, storage caps).
 *   - Upgrade in flight: `level` unchanged (current tier stays live),
 *     `buildTicksRemaining = BUILD_TICKS_FOR_LEVEL[level]`. On 0: level++.
 *     Single `buildTicksRemaining` field covers both construction and
 *     upgrade so there's one lifecycle path, not two.
 *
 * `currentHull` / `maxHull` carry the 2% auto-repair hook from STRUCTURES §4.
 * No damage source in v1 (bombardment lands later) so currentHull sits at
 * maxHull constantly — the field exists so when bombardment is wired it
 * has a target, not because it varies in v1 play.
 */
export interface Structure {
  id: EntityId;
  planetId: PlanetId;
  kind: StructureKind;
  /** Active capability level: 0 = under initial build, 1..6 = active tier. */
  level: number;
  /**
   * Ticks until the next level lands. 0 = stable at current level. >0 means
   * either initial build in progress (level = 0) or upgrade in flight
   * (level >= 1). advanceStructures decrements and promotes on zero.
   */
  buildTicksRemaining: number;
  /**
   * Sim tick when the *current* build/upgrade was queued. Set by
   * applyBuildStructure at initial build time and overwritten by
   * applyUpgradeStructure each time a new upgrade starts. Only meaningful
   * while `buildTicksRemaining > 0`; stale between builds (harmless).
   *
   * advance-structures.ts sorts in-flight structures by (queuedAt ASC,
   * id ASC) to pick which one ticks this tick — so the FIFO order reflects
   * the player's click order, not the underlying id order. Without this
   * field, upgrading an older (lower-id) structure after a newer one
   * would cause the older to jump the queue.
   */
  queuedAt: number;
  /** Current hull. Auto-repair ticks this toward maxHull at each advance. */
  currentHull: number;
  /** Max hull for the current level (derived at (re)build time). */
  maxHull: number;
  /**
   * v2M6.5 Surface Battery weapon cooldown. Ticks down each pass; on 0
   * the battery is ready to fire. Defaults 0 for non-battery structures
   * and is never read by them — the surface-batteries advance pass is
   * the only writer. Plain number, JSON-safe — saves/replays preserve
   * cooldown state without changes to serialise/deserialise.
   */
  weaponCooldownTicks: number;
  /**
   * v3M2.2 — Orbital Defence Platform ring slots. Set on the
   * `orbital-defence-platform` Structure when its first activation
   * (level 0 → 1) deploys 6 ring platforms in orbit. Length 6; each
   * slot indexes a fixed angle around the host planet. A non-null
   * entry references the live ring-platform `ShipInstance` id; a
   * null entry means the slot is awaiting rebuild (the platform was
   * destroyed and a rebuild is queued or in progress).
   *
   * `undefined` for any non-ODP structure. Optional rather than
   * required so existing Structure literals across tests don't need
   * a sweep — only the ODP advance pass reads the field. The
   * `undefined` → `defined` transition also distinguishes the first
   * activation (instant 6-platform deployment) from subsequent
   * rebuilds (one-slot-at-a-time via `rebuildQueue`).
   */
  ringPlatformShipIds?: Array<ShipInstanceId | null>;
  /**
   * v3M2.2c — pending ring-platform rebuilds for this ODP. Per
   * STRUCTURES §3.4 "Only one lost platform rebuilds at a time;
   * simultaneous losses queue." Each entry pairs a slot index (0..5,
   * matching `ringPlatformShipIds`) with a per-platform tick countdown.
   * Only the head entry ticks each pass; on completion the platform
   * spawns at the slot's angle, the entry is popped, and the next
   * entry begins ticking. Cost (Credits + Minerals) is deducted at
   * enqueue time so insufficient resources hold the slot empty rather
   * than running the timer for free.
   *
   * `undefined` for any non-ODP structure or any ODP with no pending
   * rebuilds. Same rationale as `ringPlatformShipIds` — optional to
   * avoid a fixture sweep.
   */
  rebuildQueue?: Array<{ slotIndex: number; rebuildTicksRemaining: number }>;
}

// ---------------------------------------------------------------------------
// Empires
// ---------------------------------------------------------------------------

export interface Empire {
  id: EmpireId;
  name: string;
  raceId: RaceId;
  isPlayer: boolean;
  resources: ResourcePool;
  /** Planets owned by this empire. */
  planetIds: PlanetId[];
  /**
   * Techs researched (tech names from tech-tree.json). Mutated by the
   * M12 research pass when a project completes — `advance-research.ts`
   * adds the completed tech name. All readers treat this as logically
   * readonly; the only write path is completion via advanceResearch.
   */
  researchedTechs: Set<string>;
  /**
   * The tech this empire is currently researching, or null if no target is
   * set. `rpAccumulated` tracks cumulative RP sunk into the project; on
   * reaching the tech's researchCost the tech is added to
   * `researchedTechs` and this slot clears. Setting a new target via
   * CommandSetResearchTarget resets `rpAccumulated` to 0 — research
   * progress is not preserved across target changes (M12 design lock).
   */
  activeResearch: { techName: string; rpAccumulated: number } | null;
  /** Ships owned by this empire. Replaces the old fleet grouping. */
  shipIds: ShipInstanceId[];
  /**
   * Empire display color as a CSS hex string (e.g. '#4aa0ff'). Used by the
   * renderer to tint enemy ship sprites, colored fog-of-war dots, galaxy
   * ownership rings, etc. Defaulted per-slot in scenario.ts; a future
   * game-start settings screen will let the player pick their color.
   */
  color: string;
  /**
   * v3M7.1 — AI difficulty + behaviour archetype, set at game-start time
   * (defaults Normal / Balanced when the new-game-setup screen has not
   * supplied them yet — v3M7.2 surfaces dropdowns). Player empires
   * carry the same fields for shape uniformity but the AI pass skips
   * `isPlayer === true` empires entirely, so values are inert there.
   */
  aiDifficulty: AiDifficulty;
  aiBehaviour: AiBehaviour;
  /**
   * MFix6.3 — Systems this empire has ever had a ship in (scouted /
   * occupied). Drives galaxy-view fog-of-war: unscouted systems hide
   * their planet dots, labels, and aggregate icon stacks; the system
   * detail panel shows an "Unscouted" placeholder. Persistent reveal
   * — once visited, a system stays scouted forever (classic 4X FoW).
   *
   * Initialised at scenario.ts to contain each empire's home system.
   * Updated on jump-arrival (advance.ts) and on direct in-system spawn
   * (advance-construction.ts) — any path that places a ship of this
   * empire into a system adds the system id here.
   */
  scoutedSystemIds: Set<SystemId>;
  /**
   * v3M21.1 — Empire's current diplomatic-focus target. While set,
   * per-tick Diplomatic income (sum of Embassy outputs) is routed
   * directly into the focused planet's per-empire Influence pool
   * (`Planet.influence`) instead of accumulating in the empire's
   * `resources.diplomatic` treasury. Setting null pauses the route;
   * income reverts to treasury accumulation under the existing 1000
   * cap. Player issues `setDiplomaticFocus` to change targets;
   * previously-accumulated Influence on the prior target stays put
   * (one-way ratchet — `DIPLOMACY.md §5`).
   *
   * The synthetic neutral empire (EMPIRE_NEUTRAL_ID) holds null
   * permanently — it generates no Diplomatic income to spend.
   */
  diplomaticFocus: PlanetId | null;
}

// ---------------------------------------------------------------------------
// Detection & team vision
// ---------------------------------------------------------------------------

/**
 * One entry in a team's vision map — what the team last knew about a
 * particular ship. When a target is currently visible to any of the team's
 * sensors, lastSeenTick is the current sim tick and the position fields
 * are live. When it falls out of sight, the record sticks around for
 * LAST_KNOWN_GRACE_TICKS (MECHANICS §1.4) so renderers can draw a faded
 * ghost at the last known spot and combat targeting has something to aim at.
 * After the grace window expires the record is deleted by the detection pass.
 *
 * seenVia discriminates how the target is being observed *this* tick:
 *   - 'vision' — the target fell inside the probabilistic vision ring of at
 *     least one team scanner and the roll succeeded. Renderer draws the
 *     actual ship sprite at the ship's current position.
 *   - 'scan'   — the target is beyond vision but within the banded scan
 *     envelope of a team scanner, and the roll succeeded. Renderer draws a
 *     colored dot at the last-seen position (no precise sprite).
 *   - null     — not currently spotted; the record is alive only because we
 *     are still inside the grace window. Renderer draws a faded "ghost"
 *     dot at lastSeenSx/Sy until the record expires and gets pruned.
 *
 * v2 note: deep-space sensor structures detecting enemy ships in hyperspace
 * or across system boundaries will surface as `seenVia: 'scan'`; no extra
 * enum case needed. `lastSeenSystemId` stays non-null for now — when v2
 * hyperspace detections land, this field will be extended (nullable or
 * paired with origin/target hyperspace fields) rather than reinterpreted.
 */
export interface VisibilityRecord {
  shipId: ShipInstanceId;
  /** Sim tick at which the target was most recently observed. */
  lastSeenTick: number;
  /** Which system the target was last seen in. */
  lastSeenSystemId: SystemId;
  /** Target's in-system position at the last observation (local 0..1). */
  lastSeenSx: number;
  lastSeenSy: number;
  /**
   * How the target is being observed this tick, or null when the record is
   * coasting on the grace window. See interface-level doc for channel
   * semantics and render implications.
   */
  seenVia: 'vision' | 'scan' | null;
}

/**
 * v3M19 — per-tick combat event for the floating-damage-numbers VFX
 * dispatch. Pushed by `applyVolleyToShip` once per slot fire (regardless of
 * round count) and drained by the render layer between sim ticks.
 *
 * Transient state: cleared at the start of every `advanceCombat` pass.
 * Excluded from the save serialise body (re-attached as `[]` on
 * deserialise) so saves don't carry render-only data.
 *
 * One slot fire produces at most one event, summarising the volley:
 *   - `shieldDamage` — total damage absorbed by the target's shields across
 *     all rounds in the volley. Renders as a blue floater. Zero when the
 *     target had no shields or the volley landed entirely on hull.
 *   - `hullDamage` — total damage applied to hull (post-armor) across all
 *     rounds. Renders as a red floater. Zero when shields fully soaked the
 *     volley.
 *   - `hitCount` — number of rounds that passed the per-round hit roll.
 *     Used as the `×M` multiplier on the damage floater (suppressed when
 *     `hitCount === 1`).
 *   - `missCount` — number of rounds that failed the hit roll. Renders as
 *     a white `MISS ×K` floater (multiplier suppressed when 1; floater
 *     suppressed entirely when 0).
 *   - `damageType` — captured for forward-compat with a future colour-by-
 *     type variant; the v3M19 colour scheme keys on shield-vs-hull, not
 *     damage type.
 *   - `ownerEmpireId` — the FIRING empire's id. The render-side FoW gate
 *     uses this with `canViewerSeeShip(target)` to drop events the player
 *     can't witness. (Target's empire is reachable from `targetShipId`.)
 *   - `(sx, sy)` — captured target position at volley resolution time,
 *     so the floater anchors correctly even if the target moves or is
 *     despawned before the next render frame.
 *
 * EMP weapons (`fireEmpSalvo`) emit no event — EMP doesn't deal HP damage,
 * its effect is the disable-tick stamp on `ShipInstance.disabledTicks`.
 */
export interface DamageEvent {
  targetShipId: ShipInstanceId;
  ownerEmpireId: EmpireId;
  systemId: SystemId;
  sx: number;
  sy: number;
  shieldDamage: number;
  hullDamage: number;
  hitCount: number;
  missCount: number;
  damageType: 'Energy' | 'Kinetic' | 'Concussion';
}

// ---------------------------------------------------------------------------
// v3M28 — Missiles as persistent entities
//
// See `docs/MISSILES.md` for the authoritative design. Summary: missile
// weapons enqueue a `Missile` entity at fire-time; an `advanceMissiles`
// per-tick pass walks each entry, recomputes Bezier-eased position,
// validates the target each tick, and resolves damage on arrival. Other
// weapon subtypes (Beam / Pulse / Burst / Railgun / Torpedo / EMP) keep
// the legacy instant-damage path with cosmetic tracers.
// ---------------------------------------------------------------------------

export interface Missile {
  /** Stable id from `state.nextEntityId`. */
  id: MissileId;
  /** Owning empire — drives accent colour + accuracy/dodge math. */
  ownerEmpireId: EmpireId;
  /**
   * Launching ship. Null is permitted — the missile continues
   * regardless of the shooter's fate (`MISSILES.md §7` — shooter dies
   * / shooter jumps both leave the missile in flight). The field is
   * informational (renderer hint, AI accountability).
   */
  launchedFromShipId: ShipInstanceId | null;
  /**
   * System the missile travels through. Missiles are single-system —
   * if the target jumps out, the missile despawns silently rather
   * than chase across systems.
   */
  systemId: SystemId;
  /**
   * Intended target. Validated against `state.ships` every advance
   * tick; if the target died / jumped / changed systems, the missile
   * despawns silently per `MISSILES.md §7`.
   */
  targetShipId: ShipInstanceId;
  /**
   * Catalogue weapon name (e.g. `'Pulson Missile Mk 3'`). Resolved at
   * impact time so per-Mk damage / accuracy comes from the live
   * `state.weapons` map — preserves the existing weapon-Mk dispatch
   * path.
   */
  weaponName: string;
  /** Absolute tick the missile was launched. Combined with `ttlTicks`
   *  to derive the eased `ageFrac` used for Bezier position lookup. */
  launchedAtTick: number;
  /** Ticks from launch to arrival. Per-family per `MISSILES.md §6`:
   *  Pulson 30 (3.0 s), Zeon 40 (4.0 s). Identical within a family
   *  — Mk tier differentiation lives in damage / range, not TTL. */
  ttlTicks: number;
  /** Bezier start point — launcher's `(sx, sy)` at fire-time. */
  sx0: number;
  sy0: number;
  /** Bezier end point — target's `(sx, sy)` at fire-time. Frozen at
   *  launch for **sim purposes** (determinism + replay parity).
   *  Render layer overrides this per-frame with the target ship's
   *  current `(sx, sy)` so the visible missile homes onto a moving
   *  target; misses scatter perpendicular to the current launcher→
   *  target line. See `MISSILES.md §3 / §4.1 / §5` for the visual/
   *  sim split and `drawMissiles` in `src/render/vfx.ts` for the
   *  retarget. */
  sx1: number;
  sy1: number;
  /** Bezier control point — perpendicular-offset midpoint. Computed
   *  at fire-time via `state.rng` so the arc is deterministic across
   *  save round-trips. See `MISSILES.md §4.1` for construction. */
  cx: number;
  cy: number;
  /** Accuracy roll outcome, frozen at fire-time. `true` = damage will
   *  apply on arrival; `false` = missile still travels + detonates
   *  visually but deals 0 damage. */
  willHit: boolean;
}

// ---------------------------------------------------------------------------
// Root GameState
// ---------------------------------------------------------------------------

export interface GameState {
  /** Save-format version. Increment on any breaking schema change. */
  version: number;
  /** Monotonic tick counter; sim runs at 10 ticks/sec. */
  tick: number;
  /** Seeded RNG - every mutation that needs randomness advances this. */
  rng: RngState;
  /** Entity-id generator; next entity gets this value, then it increments. */
  nextEntityId: EntityId;
  empires: Map<EmpireId, Empire>;
  planets: Map<PlanetId, Planet>;
  systems: Map<SystemId, StarSystem>;
  jumpPoints: Map<JumpPointId, JumpPoint>;
  /**
   * v3M18 — explicit player-authored Warp Gate connections. Empty at scenario
   * init; populated by `applyConnectWarpGate` and drained by
   * `applyDisconnectWarpGate` / on-destroy handling. Each entry pairs two
   * Warp Gate ShipInstances into a virtual hyperlane; see WarpGateLink for
   * lifecycle. Strictly per-empire (every link's gateA + gateB share an
   * empire); we don't keep an empire-keyed index because the Map is small
   * and direct iteration is cheap.
   */
  warpGateLinks: Map<WarpGateLinkId, WarpGateLink>;
  ships: Map<ShipInstanceId, ShipInstance>;
  /**
   * v3M28 — persistent missile entities currently in flight. Inserted
   * by `enqueueMissile` at fire-time (from the combat dispatch path
   * for any `subType === 'Missile'` weapon), walked by
   * `advanceMissiles` per tick, removed on arrival / target-gone /
   * target-jumped per `MISSILES.md §7`. Empty Map at scenario init;
   * empty Map after a battle settles. Each entry carries its own
   * Bezier trajectory + accuracy outcome so per-tick advancement is
   * a pure function of the entity + game tick. See `MISSILES.md` for
   * the authoritative design.
   */
  missiles: Map<MissileId, Missile>;
  /**
   * All planet-side structures (STRUCTURES §2). Each is referenced from its
   * owning planet's `structureIds: EntityId[]`. Space-based structures are
   * stored in `ships` per the STRUCTURES §3 "stationary-ships" classification,
   * not here — this map is planet-side only.
   */
  structures: Map<EntityId, Structure>;
  /**
   * Tactical-map environmental entities (M14). Asteroid fields and gas
   * giants are sited per system at scenario gen; Constructor-built
   * resource extractors (Asteroid Mining Outpost, Gas Giant Refinery)
   * require a matching entity under the Constructor's current position
   * at build time. Read-only after scenario init in v1.
   */
  environment: Map<EntityId, EnvironmentEntity>;
  /** Static ship templates loaded from data/ships.json. Never mutated post-load. */
  shipTemplates: ReadonlyMap<string, ShipTemplate>;
  /**
   * MFix8.5 — Aura definitions loaded from the `Auras` sheet on
   * Ships.xlsx, keyed by aura name. Emitter ships reference an aura
   * via `ShipTemplate.aura`; the aura helper looks up this map at
   * runtime to apply per-ship bonuses. Static post-load like the other
   * data catalogues; dropped from saves and re-attached on load.
   *
   * Optional rather than required so existing GameState literals
   * across tests don't need a fixture sweep — `auraBonusesForShip`
   * defensively returns the no-aura default when `state.auras` is
   * undefined or empty. Mirrors the `chassisLevel?` precedent from
   * v3M2.2.
   */
  auras?: ReadonlyMap<string, AuraDef>;
  /**
   * Static weapon catalogue loaded from data/ships.json, keyed by
   * `weapon.name` (e.g. 'Pulse Laser Mk 1'). Ship loadouts reference weapons
   * generically by *slot* (e.g. 'Energy Pulse Weapon'); the combat pass
   * resolves the slot to a concrete weapon in this map, picking the highest
   * Mk the owning empire has researched (falling back to the Mk 1 variant,
   * which has researchCost 0 — always available). Never mutated post-load.
   */
  weapons: ReadonlyMap<string, WeaponDef>;
  /**
   * Name-keyed tech catalogue from Tech_Tree.xlsx (M12). Keyed by `techName`.
   * Used by `applySetResearchTarget` to validate targets, by
   * `advance-research.ts` to look up researchCost during the drain pass,
   * and (in M12.2+) by effect helpers that read category + level for
   * Hull/Armor/Shield/Hyperdrive progressions. Never mutated post-load.
   */
  techs: ReadonlyMap<string, TechDef>;
  /**
   * MFix3.2 — Habitability catalogue from `docs/Habitability.xlsx`. Drives
   * `habitabilityFor(planet, race, researchedTechs)` and per-band population
   * dynamics in `advancePopulationAndEconomy`. Static post-load like the
   * other data catalogues; dropped from saves and re-attached on load.
   */
  habitability: HabitabilityData;
  /**
   * v3M7.1 — AI behaviours catalogue from `docs/AI_Behaviours.xlsx`. Drives
   * `getAiConfig(race, difficulty, behaviour)` in `balance-ai.ts`. Static
   * post-load; dropped from saves and re-attached on load.
   */
  aiBehaviours: AiBehavioursData;
  /**
   * v3M21.5 — race catalogue from Ships.xlsx Races sheet. Carries the
   * per-race `diplomaticAggressionMultiplier` consumed by the AI
   * dispatch loop. Optional to avoid a ~30-fixture sweep — the
   * runtime helpers default to multiplier `1.0` when absent (matches
   * the MFix8.5 `auras?` pattern). Static post-load; re-attached
   * from `LoadedGameData` on load like the other catalogues.
   */
  races?: ReadonlyArray<RaceDef>;
  /**
   * v3M14a — Structure tunables catalogue from `docs/Structures.xlsx`.
   * Per-kind build cost / build ticks / hull / max-level / race
   * overrides / unique flag / upkeep / economy multiplier, plus the
   * empire-wide flat caps and the Embassy diplomacy curve. Static
   * post-load; dropped from saves and re-attached on load like the
   * other catalogues.
   *
   * Named `structuresData` to avoid colliding with the existing
   * `state.structures: Map<EntityId, Structure>` field which holds
   * per-planet structure instances. v3M14a wires this through but
   * consumer migration (replacing the file-level constants in
   * `balance-structures.ts` with state-driven lookups) lands in
   * v3M14b.
   */
  structuresData: StructuresData;
  /**
   * v3M10.1 — cross-cutting balance constants catalogue. Static post-
   * load; dropped from saves and re-attached on load from
   * `LoadedGameData` like the other domain catalogues. See
   * `BalanceData` for the field map.
   */
  balanceData: BalanceData;
  /**
   * Per-team visibility: TeamId -> (ShipInstanceId -> VisibilityRecord). One
   * row per *target* the team currently sees or remembers within its grace
   * window. Written by the detection pass in advanceTick; read by renderers
   * (fog of war) and combat targeting.
   *
   * In v1, TeamId === EmpireId, so every empire gets its own map initialised
ion", so a
   * fresh game starts fully fogged except for an empire's own ships (which
   * are never in teamVision and always render).
   */
  teamVision: Map<TeamId, Map<ShipInstanceId, VisibilityRecord>>;
  /**
   * v3M19 — per-tick combat-event buffer for the floating-damage-numbers
   * VFX dispatch. Cleared at the start of every `advanceCombat` pass and
   * populated as the pass resolves volleys; drained by the render layer
   * between sim ticks. Transient — excluded from the save serialise body
   * and re-attached as `[]` on deserialise. See `DamageEvent` for the
   * per-event shape and the dispatch semantics.
   */
  combatEvents: DamageEvent[];
  /** Primary local player's empire id. Stable across saves. */
  playerEmpireId: EmpireId;
  /**
   * v3M12 — game-end outcome. `'in-progress'` for an active game;
   * `'victory'` when every non-player empire has been eliminated
   * (zero planets owned); `'defeat'` when the player's empire has
   * zero planets (incl. via manual Concede). Set by
   * `advance-game-end.ts` after combat / bombardment passes have
   * settled, or by `applyConcede`. While non-`'in-progress'`,
   * `advanceTick` is a no-op — the world freezes mid-frame at the
   * outcome moment so the end-game overlay can mount + animate.
   * Saved/loaded transparently; older saves without the field
   * default to `'in-progress'`.
   */
  gameOutcome: 'in-progress' | 'victory' | 'defeat';
}

/**
 * MFix1.4 bumped this from 1 → 2 when PlanetSize switched from a
 * 3-value enum to a number. Old saves stored `size: 'medium'` strings
 * which would crash on `size * 10` arithmetic; the wrapper-level
 * SAVE_BLOB_VERSION check in deserialize.ts rejects mismatched
 * blobs with a clear SaveLoadError rather than letting them load.
 *
 * MFix3.4 bumped 2 → 3 when `pendingColoniseTargetId` was added to
 * ShipInstance. Older saves don't have the field; rather than migrate,
 * the version bump rejects them up front (saves are cheap to recreate).
 *
 * v3M7.1 bumped 3 → 4 when `aiDifficulty` + `aiBehaviour` were added to
 * Empire. Same migration policy.
 *
 * v3M2.2b bumped 4 → 5 covering ODP ring state + Minefield runtime fields.
 *
 * MFix6.3 bumped 5 → 6 when `scoutedSystemIds` was added to Empire.
 * Galaxy-view fog-of-war drives off this set; old saves don't have it
 * and would render every system as unscouted otherwise. Same
 * migration policy: invalidate, don't migrate.
 *
 * v3M18.1 bumped 6 → 7 when `warpGateLinks` was added to GameState.
 * Old saves don't have the collection; rather than default-empty
 * (which would silently lose any nascent connection design intent if
 * we ever back-port), invalidate up front. Same migration policy.
 *
 * v3M18.5 bumped 7 → 8 when `jumpTargetGateId` was added to
 * ShipInstance for warp-link arrival. Same migration policy.
 *
 * v3M18.5 fix-up bumped 8 → 9 when `pendingWarpGateLinkId` was added
 * to ShipInstance to support sail-then-jump flow on warp links
 * (mirror of the existing `pendingJumpPointId` for JPs).
 *
 * v3M17.1 bumped 9 → 10 for the carrier fighter launch data model:
 * six new optional ShipInstance fields (`embarkedSquadronCount`,
 * `rebuildProgressFighters`, `carrierAttackBehaviour`,
 * `carrierDefenseBehaviour` carrier-side; `liveFighterCount`,
 * `parentCarrierShipId` squadron-side). Optional, so old saves still
 * load — old carriers come back with undefined for the carrier-side
 * fields and v3M17.2's per-tick pass treats undefined as "untracked"
 * (falls back to the chassis-derived defaults on first observation).
 * No fixture sweep needed. Same migration policy: existing saves not
 * auto-migrated and may need to be recreated for the new behaviour
 * to apply.
 */
/**
 * v3M28.1 bumped 12 → 13 when `state.missiles: Map<MissileId, Missile>`
 * was added as a first-class persistent-entity collection. Older saves
 * lack the field; rather than default to an empty Map (which would
 * silently lose any in-flight volley should we ever back-port the load
 * path), invalidate up front. Same migration policy as every prior bump
 * — pre-v13 saves fail the deserialise version gate with a clear
 * SaveLoadError. No code path migrates them.
 */
export const SAVE_FORMAT_VERSION = 13;

/**
 * v3M21.1 — Reserved EmpireId for the synthetic neutral empire.
 *
 * Owns all populated-but-unaffiliated planets seeded by scenario-gen
 * (`DIPLOMACY.md §3`) plus their pre-built defensive structures. Has
 * race `'Neutral'` and AI behaviour `'Neutral'`; takes no AI actions,
 * does not research, does not build, does not dispatch. Iteration
 * sites that walk `state.empires` for AI dispatch / turn order /
 * scoreboard / victory checks must filter via
 * `if (empire.id === EMPIRE_NEUTRAL_ID) continue;`.
 *
 * Value: 0. The default `playerEmpireId: 0` placeholder set during
 * state init is overwritten before any consumer reads it (see
 * `scenario.ts` immediately after `makeEmpire(playerEmpire)` returns),
 * and `nextEntityId: 1` keeps fresh empire ids strictly above this
 * reserved value, so no allocation collision is possible.
 */
export const EMPIRE_NEUTRAL_ID: EmpireId = 0;

/** Allocate a new entity id, mutating nextEntityId. */
export function allocEntityId(state: GameState): EntityId {
  const id = state.nextEntityId;
  state.nextEntityId = id + 1;
  return id;
}
